"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { useGuestCart } from "../../lib/use-guest-cart.js";
import { formatKopecks } from "../../lib/money.js";

type Row = { id: string; name: string; displayPriceKopecks: number | null };

// Russian item-count pluralization: 1 товар · 2 товара · 5 товаров.
function itemsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "товар";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "товара";
  return "товаров";
}

export default function CartPage() {
  const { cart } = useGuestCart();
  const [catalog, setCatalog] = useState<Row[]>([]);
  useEffect(() => {
    fetch("/api/catalog").then((r) => r.json()).then((j) => { if (j.success) setCatalog(j.data); });
  }, []);
  const lines = cart.items.map((i) => ({ ...i, product: catalog.find((c) => c.id === i.productId) }));
  const count = cart.items.reduce((s, i) => s + i.qty, 0);
  const subtotalKopecks = lines.reduce((s, l) => s + (l.product?.displayPriceKopecks ?? 0) * l.qty, 0);

  return (
    <main style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 28px" }}>
      <h1 style={{ fontSize: 28, margin: "0 0 18px" }}>Корзина</h1>
      {cart.items.length === 0 ? (
        <div
          className="card"
          style={{ maxWidth: 480, margin: "24px auto", textAlign: "center", padding: "44px 32px" }}
        >
          <div className="ph" style={{ width: 64, height: 64, borderRadius: 12, margin: "0 auto 18px" }} />
          <p
            data-testid="cart-empty"
            style={{ fontFamily: "var(--font-display)", fontSize: 22, margin: "0 0 8px" }}
          >
            Корзина пуста
          </p>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 22px" }}>
            Загляните в каталог — там есть что выбрать.
          </p>
          <Link href="/" className="btn-o" style={{ display: "inline-block" }}>В каталог</Link>
        </div>
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {/* line items */}
            <section
              style={{ flex: "1 1 360px", minWidth: 0, padding: "22px 28px 26px", borderRight: "1px solid var(--border)" }}
            >
              <div className="display" style={{ fontSize: 22, marginBottom: 6 }}>
                {count} {itemsWord(count)}
              </div>
              <ul aria-label="Позиции корзины" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {lines.map((l) => {
                  const price = l.product?.displayPriceKopecks ?? null;
                  return (
                    <li
                      key={l.productId}
                      data-testid={`cart-item-${l.productId}`}
                      style={{
                        display: "flex",
                        gap: 16,
                        padding: "16px 0",
                        borderTop: "1px solid var(--border)",
                        alignItems: "center",
                      }}
                    >
                      <div className="ph" style={{ width: 72, height: 72, borderRadius: 6, flex: "none" }}>фото</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{l.product?.name ?? l.productId}</div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                          {price === null ? "Цена по запросу" : `${formatKopecks(price)} / шт.`}
                        </div>
                      </div>
                      <span className="qty" style={{ flex: "none" }}><span>× {l.qty}</span></span>
                      <span className="price" style={{ fontWeight: 600, width: 96, textAlign: "right", flex: "none" }}>
                        {price === null ? "—" : formatKopecks(price * l.qty)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
            {/* summary */}
            <aside className="panel2" style={{ flex: "1 1 260px", minWidth: 0, padding: "26px 32px" }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Итог</div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", fontSize: 13 }}>
                <span>Товары ({count})</span>
                <span className="mono">{formatKopecks(subtotalKopecks)}</span>
              </div>
              <div className="muted" style={{ fontSize: 12, padding: "7px 0", lineHeight: 1.5 }}>
                Доставка и скидки рассчитываются при оформлении
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop: "1px solid var(--border)",
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 15 }}>Итого</span>
                <span className="price" style={{ fontSize: 26 }}>{formatKopecks(subtotalKopecks)}</span>
              </div>
              <a
                href="/checkout"
                data-testid="to-checkout"
                className="btn-p"
                style={{ display: "block", textAlign: "center", marginTop: 18, padding: 14 }}
              >
                Оформить заказ
              </a>
            </aside>
          </div>
        </div>
      )}
    </main>
  );
}
